/**
 * A logo preview that says so when the file will not load.
 *
 * A broken <img> renders as nothing, so a dead URL looked like a blank tile and
 * a white mark on a light backdrop looked the same. On error this shows a
 * labelled placeholder with the URL as its tooltip.
 */

import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { bvColor, bvRadius, bvType } from '../brandVendorsTokens';

export type LogoPreviewBackground = 'light' | 'dark';

/** Backdrop styles from theme tokens: the checkerboard for light, solid ink for dark. */
export const logoPreviewBackdrop = (mode: LogoPreviewBackground) =>
  mode === 'dark'
    ? { bgcolor: bvColor.ink }
    : {
        backgroundImage: `linear-gradient(45deg, ${bvColor.borderSubtle} 25%, transparent 25%, transparent 75%, ${bvColor.borderSubtle} 75%),
    linear-gradient(45deg, ${bvColor.borderSubtle} 25%, transparent 25%, transparent 75%, ${bvColor.borderSubtle} 75%)`,
        backgroundSize: '12px 12px',
        backgroundPosition: '0 0, 6px 6px',
      };

export const LogoPreviewImage: React.FC<{
  src: string;
  maxWidth: number;
  maxHeight: number;
  filter?: string;
  opacity?: number;
  /** Show the words under the icon; thumbnails get the icon and tooltip only. */
  compact?: boolean;
}> = ({ src, maxWidth, maxHeight, filter = 'none', opacity = 1, compact = false }) => {
  const [failed, setFailed] = useState(false);

  // A replaced upload gets a new URL; give it a fresh chance to load.
  useEffect(() => setFailed(false), [src]);

  if (failed) {
    return (
      <Box
        role="img"
        aria-label="Image failed to load"
        title={`Image failed to load: ${src}`}
        sx={{
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          px: '4px',
          py: '2px',
          border: `1px dashed ${bvColor.dangerBorder}`,
          borderRadius: `${bvRadius.small}px`,
          bgcolor: bvColor.dangerBg,
          color: bvColor.danger,
          ...bvType.microCaption,
          lineHeight: 1.2,
        }}
      >
        {compact ? '!' : 'Image failed to load'}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
      sx={{ maxWidth, maxHeight, objectFit: 'contain', filter, opacity }}
    />
  );
};
